import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import api from '../../api/client';
import { Button, StatusBadge } from '../common';
import { formatDate, getUserName, isOverdue } from '../../utils/formatters';

/**
 * TodoDetailsModal - Read-only display of task details
 * Fetches full todo data including topic details from the API
 */
const TodoDetailsModal = ({ todoId, onClose }) => {
  const [todo, setTodo] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const loadTodoData = async () => {
      try {
        const [todoData, usersData] = await Promise.all([
          api.get(`/todos/${todoId}`),
          api.get('/users/list')
        ]);
        setTodo(todoData);
        setUsers(usersData);
      } catch (error) {
        console.error('Failed to load todo details:', error);
      } finally {
        setLoading(false);
      }
    };
    loadTodoData();
  }, [todoId]);

  if (loading) {
    return (
      <div className="modal-overlay">
        <div className="modal modal-xl">
          <div className="modal-body">
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!todo) return null;

  const assigneeNames = todo.assignees
    ? Array.isArray(todo.assignees)
      ? typeof todo.assignees[0] === 'object'
        ? todo.assignees.map(a => `${a.firstName} ${a.lastName}`).join(', ')
        : todo.assignees.map(id => getUserName(id, users)).join(', ')
      : 'Unassigned'
    : 'Unassigned';

  const overdueCheck = isOverdue(todo);
  const notesCount = todo.notes ? todo.notes.length : 0;
  const historyCount = todo.dueDateHistory ? todo.dueDateHistory.length : 0;

  return (
    <div className="modal-overlay">
      <div className="modal modal-xl">
        <div className="modal-header">
          <h2 className="modal-title">Task Details</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-tabs">
          <button
            className={`modal-tab-btn${activeTab === 'overview' ? ' active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            className={`modal-tab-btn${activeTab === 'notes' ? ' active' : ''}`}
            onClick={() => setActiveTab('notes')}
          >
            Notes{notesCount > 0 ? ` (${notesCount})` : ''}
          </button>
          <button
            className={`modal-tab-btn${activeTab === 'history' ? ' active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            Due Date History{historyCount > 0 ? ` (${historyCount})` : ''}
          </button>
        </div>
        <div className="modal-body">
          {activeTab === 'overview' && (
            <div>
              <h3 style={{ fontSize: '1.25rem', marginBottom: '1rem', color: 'var(--color-primary-dark)' }}>
                {todo.title}
              </h3>
              <table className="data-table" style={{ marginBottom: '1.25rem' }}>
                <tbody>
                  <tr>
                    <td style={{ fontWeight: 'bold', width: '150px' }}>Status</td>
                    <td><StatusBadge status={todo.status} type="todo" /></td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 'bold' }}>Assigned To</td>
                    <td>{assigneeNames}</td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 'bold' }}>Created</td>
                    <td>{new Date(todo.createdAt).toLocaleString()}</td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 'bold' }}>Due Date</td>
                    <td>
                      {todo.dueDate ? (
                        <span style={{
                          color: overdueCheck ? 'var(--color-danger)' : 'inherit',
                          fontWeight: overdueCheck ? 'bold' : 'normal'
                        }}>
                          {formatDate(todo.dueDate)}
                          {overdueCheck && ' ⚠️ OVERDUE'}
                        </span>
                      ) : 'No due date'}
                    </td>
                  </tr>
                  {todo.meeting && (
                    <tr>
                      <td style={{ fontWeight: 'bold' }}>Meeting</td>
                      <td>{todo.meeting.name}</td>
                    </tr>
                  )}
                </tbody>
              </table>
              {todo.details && (
                <div style={{ marginBottom: '1.25rem' }}>
                  <h4 style={{ fontSize: '0.9rem', fontWeight: 'bold', marginBottom: '0.4rem', color: 'var(--color-primary)' }}>
                    Task Details
                  </h4>
                  <div style={{
                    padding: '0.75rem',
                    background: 'var(--color-bg)',
                    borderRadius: '4px',
                    whiteSpace: 'pre-wrap',
                    fontSize: '0.9rem'
                  }}>
                    {todo.details}
                  </div>
                </div>
              )}
              {todo.topic && (
                <div>
                  <h4 style={{ fontSize: '0.9rem', fontWeight: 'bold', marginBottom: '0.4rem', color: 'var(--color-primary)' }}>
                    Related Topic
                  </h4>
                  <div style={{
                    padding: '0.75rem',
                    background: 'var(--color-surface)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '4px',
                    borderLeft: '3px solid var(--color-primary)'
                  }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '0.35rem', fontSize: '0.95rem' }}>
                      {todo.topic.title}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginBottom: '0.5rem' }}>
                      From: {todo.meeting?.name || 'Unknown Meeting'} · Submitted by {todo.topic.creatorName}
                    </div>
                    {todo.topic.description && (
                      <div
                        style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', marginTop: '0.5rem', whiteSpace: 'pre-wrap' }}
                        dangerouslySetInnerHTML={{ __html: todo.topic.description }}
                      />
                    )}
                    {todo.topic.discussionNotes && (
                      <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid var(--color-border)' }}>
                        <div style={{ fontWeight: 'bold', fontSize: '0.85rem', marginBottom: '0.35rem' }}>
                          Discussion Notes:
                        </div>
                        <div
                          style={{
                            fontSize: '0.85rem',
                            color: 'var(--color-text)',
                            whiteSpace: 'pre-wrap',
                            background: 'var(--color-bg)',
                            padding: '0.5rem',
                            borderRadius: '4px'
                          }}
                        >
                          {todo.topic.discussionNotes}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'notes' && (
            <div>
              {todo.notes && todo.notes.length > 0 ? (
                <div style={{
                  border: '1px solid var(--color-border)',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  overflow: 'hidden',
                }}>
                  {todo.notes.map((note, idx) => (
                    <div
                      key={note.id}
                      style={{
                        padding: '0.6rem 0.75rem',
                        borderBottom: idx < todo.notes.length - 1 ? '1px solid var(--color-border)' : 'none',
                      }}
                    >
                      <div style={{ whiteSpace: 'pre-wrap', marginBottom: '0.25rem' }}>{note.content}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                        {note.createdByName} &mdash; {new Date(note.createdAt).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: 'var(--color-text-muted)' }}>No notes yet.</p>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div style={{ fontSize: '0.85rem' }}>
              {todo.dueDateHistory && todo.dueDateHistory.length > 0 ? (
                todo.dueDateHistory.map((entry, idx) => (
                  <div key={idx} style={{ marginBottom: idx < todo.dueDateHistory.length - 1 ? '0.5rem' : 0 }}>
                    <strong>{new Date(entry.changedAt).toLocaleString()}:</strong>{' '}
                    {entry.previousDate ? formatDate(entry.previousDate) : 'No date'} → {entry.date ? formatDate(entry.date) : 'No date'}
                    {entry.changedByName && ` (by ${entry.changedByName})`}
                  </div>
                ))
              ) : (
                <span style={{ color: 'var(--color-text-muted)' }}>No due date changes recorded.</span>
              )}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
};

TodoDetailsModal.propTypes = {
  todoId: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default TodoDetailsModal;
